import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MaterialReactTable,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton,
  useMaterialReactTable,
} from 'material-react-table';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

const emptyLine = {
  category: '',
  easyRedmineSkill: '',
  easyRedmineLevel: '',
  profile: 'admin',
  active: true,
};

const buildHeaderForm = (editing) => ({
  name: editing?.name || '',
  description: editing?.description || '',
});

const buildLineForm = (editing) => ({
  _id: editing?._id,
  category: editing?.category || '',
  easyRedmineSkill: editing?.easyRedmineSkill || editing?.competence || '',
  easyRedmineLevel: editing?.easyRedmineLevel || '',
  profile: editing?.profile || 'admin',
  active: editing?.active !== false,
});

function MatrixHeaderDialog({ open, editing, onClose, onSaved, api }) {
  const [form, setForm] = useState(buildHeaderForm(editing));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(buildHeaderForm(editing));
      setError('');
    }
  }, [editing, open]);

  const handleSubmit = async () => {
    setSaving(true);
    setError('');

    try {
      const payload = {
        name: form.name,
        description: form.description,
        competences: editing?.competences || [],
      };

      if (editing?._id) {
        await api.put(`/api/contrat/matrix/${editing._id}`, payload);
      } else {
        await api.post('/api/contrat/matrix', payload);
      }

      onSaved();
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || e.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{editing?._id ? 'Modifier la matrice' : 'Nouvelle matrice'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            label="Nom de la matrice"
            value={form.name}
            onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))}
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm((current) => ({ ...current, description: e.target.value }))}
            multiline
            minRows={2}
          />
          {error ? <Typography color="error">{error}</Typography> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function MatrixLineDialog({
  open,
  matrix,
  editing,
  onClose,
  onSaved,
  competenceCatalog,
}) {
  const [form, setForm] = useState(buildLineForm(editing));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [addAnother, setAddAnother] = useState(false);

  const skillOptions = competenceCatalog?.competence1?.possible_values || [];
  const dependencyMap = useMemo(() => {
    const nextMap = new Map();
    (competenceCatalog?.dependencies || []).forEach((item) => {
      nextMap.set(item.value, item.children || []);
    });
    return nextMap;
  }, [competenceCatalog]);

  useEffect(() => {
    if (open) {
      setForm(buildLineForm(editing));
      setError('');
      setAddAnother(false);
    }
  }, [editing, open]);

  const getLevelOptions = (skill) => dependencyMap.get(skill) || [];

  const buildLinePayload = () => ({
    ...(form._id ? { _id: form._id } : {}),
    category: form.category || '',
    competence: form.easyRedmineSkill || '',
    detail: form.category || '',
    level: form.profile || '',
    profile: form.profile || 'admin',
    easyRedmineSkill: form.easyRedmineSkill || '',
    easyRedmineLevel: form.easyRedmineLevel || '',
    matrixLevel: form.profile || '',
    active: form.active !== false,
  });

  const handleSave = async () => {
    if (!matrix?._id) return;

    setSaving(true);
    setError('');

    try {
      const nextLine = buildLinePayload();
      const existingLines = Array.isArray(matrix.competences) ? [...matrix.competences] : [];
      const nextLines = form._id
        ? existingLines.map((item) => (String(item._id) === String(form._id) ? { ...item, ...nextLine } : item))
        : [...existingLines, nextLine];

      await onSaved({
        ...matrix,
        competences: nextLines,
      });

      if (addAnother && !form._id) {
        setForm({ ...emptyLine, category: form.category });
        setError('');
      } else {
        onClose();
      }
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || e.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{form._id ? 'Modifier une ligne de matrice' : 'Ajouter une ligne de matrice'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {!competenceCatalog ? (
            <Alert severity="info">
              Les listes EasyRedmine ne sont pas encore disponibles. La saisie manuelle reste possible.
            </Alert>
          ) : null}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label="Categorie"
              value={form.category}
              onChange={(e) => setForm((current) => ({ ...current, category: e.target.value }))}
            />
            <TextField
              fullWidth
              label="Competence 1"
              select={skillOptions.length > 0}
              value={form.easyRedmineSkill || ''}
              onChange={(e) => {
                const nextSkill = e.target.value;
                const nextLevels = getLevelOptions(nextSkill);
                const currentLevel = form.easyRedmineLevel || '';
                const hasCurrentLevel = nextLevels.some((option) => option.value === currentLevel);

                setForm((current) => ({
                  ...current,
                  easyRedmineSkill: nextSkill,
                  easyRedmineLevel: hasCurrentLevel ? current.easyRedmineLevel : '',
                }));
              }}
              SelectProps={skillOptions.length > 0 ? { native: true } : undefined}
            >
              {skillOptions.length > 0
                ? [
                    <option key="empty" value="">
                      --
                    </option>,
                    ...skillOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label || option.value}
                      </option>
                    )),
                  ]
                : null}
            </TextField>
            <TextField
              fullWidth
              label="Competence 2"
              select={getLevelOptions(form.easyRedmineSkill).length > 0}
              value={form.easyRedmineLevel || ''}
              onChange={(e) => setForm((current) => ({ ...current, easyRedmineLevel: e.target.value }))}
              SelectProps={getLevelOptions(form.easyRedmineSkill).length > 0 ? { native: true } : undefined}
            >
              {getLevelOptions(form.easyRedmineSkill).length > 0
                ? [
                    <option key="empty" value="">
                      --
                    </option>,
                    ...getLevelOptions(form.easyRedmineSkill).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label || option.value}
                      </option>
                    )),
                  ]
                : null}
            </TextField>
            <TextField
              fullWidth
              select
              label="Niveau"
              value={form.profile || 'admin'}
              onChange={(e) => setForm((current) => ({ ...current, profile: e.target.value }))}
              SelectProps={{ native: true }}
            >
              <option value="admin">Admin</option>
              <option value="ingenieur">Ingenieur</option>
              <option value="pilotage">Pilotage</option>
            </TextField>
            <TextField
              fullWidth
              select
              label="Active"
              value={form.active !== false ? 'true' : 'false'}
              onChange={(e) => setForm((current) => ({ ...current, active: e.target.value === 'true' }))}
              SelectProps={{ native: true }}
            >
              <option value="true">Oui</option>
              <option value="false">Non</option>
            </TextField>
          </Stack>

          {!form._id ? (
            <FormControlLabel
              control={
                <Checkbox checked={addAnother} onChange={(e) => setAddAnother(e.target.checked)} />
              }
              label="Enregistrer puis ajouter une autre ligne"
            />
          ) : null}

          {error ? <Typography color="error">{error}</Typography> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Enregistrement...' : form._id ? 'Enregistrer la ligne' : 'Ajouter la ligne'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function ContratMatrixManager({ open = false, onClose, api, onChanged, inline = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [competenceCatalog, setCompetenceCatalog] = useState(null);
  const [selectedMatrixId, setSelectedMatrixId] = useState('');
  const [headerEditorOpen, setHeaderEditorOpen] = useState(false);
  const [lineEditorOpen, setLineEditorOpen] = useState(false);
  const [headerEditing, setHeaderEditing] = useState(null);
  const [lineEditing, setLineEditing] = useState(null);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/contrat/matrix');
      const nextRows = Array.isArray(data) ? data : [];
      setRows(nextRows);
      setSelectedMatrixId((current) => {
        if (current && nextRows.some((item) => String(item._id) === String(current))) {
          return current;
        }
        return nextRows[0]?._id || '';
      });
    } finally {
      setLoading(false);
    }
  }, [api]);

  const saveMatrix = useCallback(
    async (matrix) => {
      const payload = {
        name: matrix.name,
        description: matrix.description,
        competences: matrix.competences || [],
      };
      await api.put(`/api/contrat/matrix/${matrix._id}`, payload);
      await loadRows();
      onChanged?.();
    },
    [api, loadRows, onChanged]
  );

  useEffect(() => {
    if (inline || open) loadRows();
  }, [inline, loadRows, open]);

  useEffect(() => {
    if (!(inline || open)) return undefined;

    let active = true;
    const loadCompetenceCatalog = async () => {
      try {
        const { data } = await api.get('/api/easyredmine/custom-fields/competences');
        if (active) {
          setCompetenceCatalog(data?.data || null);
        }
      } catch (e) {
        if (active) {
          setCompetenceCatalog(null);
        }
      }
    };

    loadCompetenceCatalog();
    return () => {
      active = false;
    };
  }, [api, inline, open]);

  const selectedMatrix = useMemo(
    () => rows.find((item) => String(item._id) === String(selectedMatrixId)) || null,
    [rows, selectedMatrixId]
  );

  const matrixSummaryColumns = useMemo(
    () => [
      { accessorKey: 'name', header: 'Matrice' },
      { accessorKey: 'description', header: 'Description' },
      {
        id: 'lineCount',
        header: 'Lignes',
        Cell: ({ row }) => row.original.competences?.length || 0,
      },
      {
        id: 'categories',
        header: 'Categories',
        Cell: ({ row }) => {
          const values = Array.from(
            new Set((row.original.competences || []).map((item) => item.category).filter(Boolean))
          );
          return values.length > 0 ? values.join(', ') : '—';
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        Cell: ({ row }) => (
          <Stack direction="row" spacing={1}>
            <Tooltip title="Modifier la matrice">
              <IconButton
                size="small"
                onClick={() => {
                  setSelectedMatrixId(row.original._id);
                  setHeaderEditing(row.original);
                  setHeaderEditorOpen(true);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Supprimer la matrice">
              <IconButton
                size="small"
                color="error"
                onClick={async () => {
                  if (!window.confirm(`Supprimer la matrice "${row.original.name}" ?`)) return;
                  await api.delete(`/api/contrat/matrix/${row.original._id}`);
                  await loadRows();
                  onChanged?.();
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [api, loadRows, onChanged]
  );

  const matrixSummaryTable = useMaterialReactTable({
    columns: matrixSummaryColumns,
    data: rows,
    state: { isLoading: loading },
    enablePagination: false,
    initialState: { density: 'compact' },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => setSelectedMatrixId(row.original._id),
      sx: {
        cursor: 'pointer',
        backgroundColor:
          String(row.original._id) === String(selectedMatrixId) ? 'rgba(31, 78, 95, 0.08)' : 'inherit',
      },
    }),
    renderToolbarInternalActions: ({ table }) => (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
      </Box>
    ),
  });

  const matrixLineRows = useMemo(
    () =>
      (selectedMatrix?.competences || []).map((item, index) => ({
        ...item,
        rowIndex: index + 1,
      })),
    [selectedMatrix]
  );

  const matrixLineColumns = useMemo(
    () => [
      { accessorKey: 'rowIndex', header: '#' },
      { accessorKey: 'category', header: 'Categorie' },
      {
        accessorKey: 'easyRedmineSkill',
        header: 'Competence 1',
        Cell: ({ row }) => row.original.easyRedmineSkill || row.original.competence || '—',
      },
      {
        accessorKey: 'easyRedmineLevel',
        header: 'Competence 2',
        Cell: ({ row }) => row.original.easyRedmineLevel || '—',
      },
      {
        accessorKey: 'profile',
        header: 'Niveau',
        Cell: ({ row }) => {
          const value = row.original.profile || '—';
          return (
            <Chip
              size="small"
              label={value}
              color={value === 'ingenieur' ? 'success' : value === 'pilotage' ? 'warning' : 'default'}
            />
          );
        },
      },
      {
        accessorKey: 'active',
        header: 'Active',
        Cell: ({ row }) => (row.original.active !== false ? 'Oui' : 'Non'),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        Cell: ({ row }) => (
          <Stack direction="row" spacing={1}>
            <Tooltip title="Modifier la ligne">
              <IconButton
                size="small"
                onClick={() => {
                  setLineEditing(row.original);
                  setLineEditorOpen(true);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Supprimer la ligne">
              <IconButton
                size="small"
                color="error"
                onClick={async () => {
                  if (!selectedMatrix?._id) return;
                  const label = row.original.easyRedmineSkill || row.original.competence || 'cette ligne';
                  if (!window.confirm(`Supprimer la ligne "${label}" ?`)) return;

                  await saveMatrix({
                    ...selectedMatrix,
                    competences: (selectedMatrix.competences || []).filter(
                      (item) => String(item._id) !== String(row.original._id)
                    ),
                  });
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [saveMatrix, selectedMatrix]
  );

  const matrixLineTable = useMaterialReactTable({
    columns: matrixLineColumns,
    data: matrixLineRows,
    enablePagination: false,
    initialState: { density: 'compact' },
    renderToolbarInternalActions: ({ table }) => (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
      </Box>
    ),
  });

  const content = (
    <Box sx={{ width: inline ? '100%' : { xs: '100vw', xl: 1400 }, p: inline ? 0 : 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant={inline ? 'subtitle1' : 'h6'}>Matrices de competences</Typography>
          <Typography variant="body2" color="text.secondary">
            Structure la correspondance `Competence 1 / Competence 2 / Niveau`, puis declinaisons par contrat.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setHeaderEditing(null);
            setHeaderEditorOpen(true);
          }}
        >
          Nouvelle matrice
        </Button>
      </Stack>

      <Paper sx={{ mb: 2 }}>
        <MaterialReactTable table={matrixSummaryTable} />
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="subtitle1">
              {selectedMatrix ? `Lignes de la matrice ${selectedMatrix.name}` : 'Lignes de matrice'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Edition ligne par ligne avec ajout rapide sans recharger toute la matrice.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            {selectedMatrix ? (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => {
                  setHeaderEditing(selectedMatrix);
                  setHeaderEditorOpen(true);
                }}
              >
                Modifier la matrice
              </Button>
            ) : null}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!selectedMatrix}
              onClick={() => {
                setLineEditing(null);
                setLineEditorOpen(true);
              }}
            >
              Ajouter une ligne
            </Button>
          </Stack>
        </Stack>

        {selectedMatrix ? (
          <MaterialReactTable table={matrixLineTable} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Selectionne une matrice pour modifier ses lignes.
          </Typography>
        )}
      </Paper>

      <MatrixHeaderDialog
        open={headerEditorOpen}
        editing={headerEditing}
        onClose={() => setHeaderEditorOpen(false)}
        onSaved={async () => {
          setHeaderEditorOpen(false);
          await loadRows();
          onChanged?.();
        }}
        api={api}
      />

      <MatrixLineDialog
        open={lineEditorOpen}
        matrix={selectedMatrix}
        editing={lineEditing}
        onClose={() => setLineEditorOpen(false)}
        onSaved={async (nextMatrix) => {
          await saveMatrix(nextMatrix);
          setLineEditing(null);
        }}
        competenceCatalog={competenceCatalog}
      />
    </Box>
  );

  if (inline) return content;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      {content}
    </Drawer>
  );
}
